"use client";

import React, { useState, useEffect, Suspense, lazy, useRef } from 'react';
import Footer from '@/components/layout/Footer';
import RedirectOverlay from '@/components/sections/RedirectOverlay';
import dynamic from 'next/dynamic';
import MobileTaskbar from '@/components/layout/MobileTaskbar';
import { useModal } from '@/context/ModalContext';
import { useSettings } from '@/context/SettingsContext';
import { useScroll } from '@/context/ScrollContext';
import { MODAL_IDS } from '@/constants';
import { CogIcon, LightbulbIcon, ChevronUpIcon, CheckIcon } from '@/components/ui/Icons';
import { SITE_NAME } from '@/config';

const VantaFogBackground = dynamic(() => import('@/components/sections/VantaFogBackground'), { ssr: false });

// Lazy load components for better initial performance
const DesktopNavbar = lazy(() => import('@/components/layout/DesktopNavbar'));
const TipsModal = lazy(() => import('@/components/modals/TipsModal'));
const AboutModal = lazy(() => import('@/components/modals/AboutModal'));
const PrivacyModal = lazy(() => import('@/components/modals/PrivacyModal'));
const TermsModal = lazy(() => import('@/components/modals/TermsModal'));
const ContactModal = lazy(() => import('@/components/modals/ContactModal'));
const SettingsModal = lazy(() => import('@/components/modals/SettingsModal'));
const NetworkStatusIndicator = lazy(() => import('@/components/sections/NetworkStatusIndicator'));
const InstallPrompt = lazy(() => import('@/components/modals/InstallPrompt'));
const UpdateNotification = lazy(() => import('@/components/modals/UpdateNotification'));

interface BeforeInstallPromptEvent extends Event {
    readonly platforms: string[];
    readonly userChoice: Promise<{
        outcome: 'accepted' | 'dismissed';
        platform: string;
    }>;
    prompt(): Promise<void>;
}

export default function GlobalShell({ children }: { children: React.ReactNode }) {
    const [showPreloader, setShowPreloader] = useState(true);
    const [isPreloaderLoaded, setIsPreloaderLoaded] = useState(false);

    useEffect(() => {
        // Start fade-out after 800ms
        const fadeTimer = setTimeout(() => {
            setIsPreloaderLoaded(true);
        }, 800);

        // Completely unmount after transition completes (800ms load + 800ms transition = 1600ms)
        const unmountTimer = setTimeout(() => {
            setShowPreloader(false);
        }, 1600);

        return () => {
            clearTimeout(fadeTimer);
            clearTimeout(unmountTimer);
        };
    }, []);


    const [showScrollTop, setShowScrollTop] = useState(false);
    const [isScrolled, setIsScrolled] = useState(false);
    const [deferredPrompt, setDeferredPrompt] = useState<BeforeInstallPromptEvent | null>(null);
    const [showInstallPrompt, setShowInstallPrompt] = useState(false);
    const [showInstallSuccess, setShowInstallSuccess] = useState(false);
    const [isUpdateAvailable, setIsUpdateAvailable] = useState(false);
    const waitingWorkerRef = useRef<ServiceWorker | null>(null);

    // Track PWA installation progress
    const [installStatus, _setInstallStatus] = useState<'idle' | 'installing' | 'installed'>('idle');
    const installStatusRef = useRef<'idle' | 'installing' | 'installed'>('idle');
    const setInstallStatus = (status: 'idle' | 'installing' | 'installed') => {
        _setInstallStatus(status);
        installStatusRef.current = status;
    };
    const installStartTime = useRef<number>(0);

    const { activeModal, closeModal, openModal } = useModal();
    const { resolvedTheme, isBackgroundAnimationEnabled, isRevealAnimationEnabled } = useSettings();
    const { visibleSection } = useScroll();

    // PWA: Register Service Worker in production, and unregister in development to avoid hydration/caching conflicts
    useEffect(() => {
        if (typeof window !== 'undefined' && 'serviceWorker' in navigator) {
            if (process.env.NODE_ENV === 'production') {
                const handleServiceWorkerRegistration = () => {
                    navigator.serviceWorker.register('/sw.js').then(registration => {
                        console.log('SW registered: ', registration);

                        registration.onupdatefound = () => {
                            const installingWorker = registration.installing;
                            if (installingWorker) {
                                installingWorker.onstatechange = () => {
                                    if (installingWorker.state === 'installed') {
                                        if (navigator.serviceWorker.controller) {
                                            console.log('New content is available, show notification.');
                                            waitingWorkerRef.current = installingWorker;
                                            setIsUpdateAvailable(true);
                                        }
                                    }
                                };
                            }
                        };
                    }).catch(registrationError => {
                        console.log('SW registration failed: ', registrationError);
                    });
                };

                let refreshing: boolean;
                navigator.serviceWorker.addEventListener('controllerchange', () => {
                    if (refreshing) return;
                    window.location.reload();
                    refreshing = true;
                });

                if (document.readyState === 'complete') {
                    handleServiceWorkerRegistration();
                } else {
                    window.addEventListener('load', handleServiceWorkerRegistration);
                    return () => window.removeEventListener('load', handleServiceWorkerRegistration);
                }
            } else {
                // In development mode, clean up any active service worker registrations from port 3000
                navigator.serviceWorker.getRegistrations().then(registrations => {
                    for (const registration of registrations) {
                        registration.unregister().then(unregistered => {
                            if (unregistered) {
                                console.log('Successfully unregistered developer service worker to prevent hydration conflicts.');
                                window.location.reload();
                            }
                        });
                    }
                });
            }
        }
    }, []);

    // PWA: Listen for install prompt and successful installation
    useEffect(() => {
        const handleBeforeInstallPrompt = (e: Event) => {
            e.preventDefault();
            setDeferredPrompt(e as BeforeInstallPromptEvent);
            setShowInstallPrompt(true); // Show the custom install prompt
        };

        const handleAppInstalled = () => {
            const now = Date.now();
            const timeSinceStart = now - installStartTime.current;

            // If running inside the standalone app, do not display any toast
            const isStandalone = window.matchMedia('(display-mode: standalone)').matches || (navigator as any).standalone;
            if (isStandalone) {
                console.log('PWA installation event ignored inside standalone mode');
                setShowInstallPrompt(false);
                setDeferredPrompt(null);
                return;
            }

            if (installStatusRef.current === 'idle') {
                // Fired from browser menu/native address bar action directly
                installStartTime.current = now;
                setInstallStatus('installing');
                setShowInstallSuccess(true);
                setTimeout(() => {
                    if (installStatusRef.current === 'installing') {
                        setShowInstallSuccess(false);
                    }
                }, 4000);
            } else if (installStatusRef.current === 'installing') {
                // On mobile/Android, the first appinstalled event fires immediately, and the second fires when OS registration completes.
                // On Desktop, it only fires once.
                const isMobile = /Android|webOS|iPhone|iPad|iPod|BlackBerry|IEMobile|Opera Mini/i.test(navigator.userAgent);
                
                if (isMobile) {
                    if (timeSinceStart < 2000) {
                        console.log('Android PWA: First appinstalled event (adding shortcut in progress)');
                        return;
                    } else {
                        console.log('Android PWA: Second appinstalled event (installation fully completed)');
                        setInstallStatus('installed');
                        setShowInstallSuccess(true);
                        setTimeout(() => {
                            setShowInstallSuccess(false);
                        }, 5000);
                    }
                } else {
                    console.log('Desktop PWA: Installation complete');
                    setInstallStatus('installed');
                    setShowInstallSuccess(true);
                    setTimeout(() => {
                        setShowInstallSuccess(false);
                    }, 5000);
                }
            }

            setShowInstallPrompt(false);
            setDeferredPrompt(null);
        };

        window.addEventListener('beforeinstallprompt', handleBeforeInstallPrompt);
        window.addEventListener('appinstalled', handleAppInstalled);

        return () => {
            window.removeEventListener('beforeinstallprompt', handleBeforeInstallPrompt);
            window.removeEventListener('appinstalled', handleAppInstalled);
        };
    }, []);

    useEffect(() => {
        const handleScroll = () => {
            setShowScrollTop(window.scrollY > 300);
            setIsScrolled(window.scrollY > 0);
        };
        window.addEventListener('scroll', handleScroll);
        return () => window.removeEventListener('scroll', handleScroll);
    }, []);



    // Add/remove class from root based on reveal animation setting for global CSS override
    useEffect(() => {
        const root = window.document.documentElement;
        if (!isRevealAnimationEnabled) {
            root.classList.add('animations-disabled');
        } else {
            root.classList.remove('animations-disabled');
        }
    }, [isRevealAnimationEnabled]);

    const handleInstallClick = async () => {
        if (!deferredPrompt) {
            return;
        }
        deferredPrompt.prompt();
        const { outcome } = await deferredPrompt.userChoice;
        console.log(`User response to the install prompt: ${outcome}`);
        
        if (outcome === 'accepted') {
            installStartTime.current = Date.now();
            setInstallStatus('installing');
            setShowInstallSuccess(true);
            setTimeout(() => {
                if (installStatusRef.current === 'installing') {
                    setShowInstallSuccess(false);
                }
            }, 4000); // Auto hide installing toast after 4s if appinstalled doesn't fire
        }
        
        setDeferredPrompt(null);
        setShowInstallPrompt(false);
    };

    const handleUpdateClick = () => {
        if (waitingWorkerRef.current) {
            waitingWorkerRef.current.postMessage({ type: 'SKIP_WAITING' });
            setIsUpdateAvailable(false);
        }
    };

    const scrollToTop = () => {
        window.scrollTo({ top: 0, behavior: 'smooth' });
    };

    return (
        <>
            {showPreloader && (
                <div className={`preloader ${isPreloaderLoaded ? 'loaded' : ''}`}>
                    <div className="preloader-content">
                        <h1 className="animated-logo-text text-4xl sm:text-5xl md:text-7xl" data-text={SITE_NAME}>
                            {SITE_NAME.split('').map((char, index) => (
                                <span key={index}>{char}</span>
                            ))}
                        </h1>
                        <div className="typing-indicator">
                            <span></span>
                            <span></span>
                            <span></span>
                        </div>
                    </div>
                </div>
            )}

            <Suspense fallback={null}>
                <NetworkStatusIndicator />
            </Suspense>

            <VantaFogBackground theme={resolvedTheme} isEnabled={isBackgroundAnimationEnabled} />
            <RedirectOverlay />

            <Suspense fallback={null}>
                <DesktopNavbar
                    isScrolled={isScrolled}
                    visibleSection={visibleSection}
                />
                {activeModal === MODAL_IDS.TIPS && <TipsModal isOpen={true} onClose={closeModal} />}
                {activeModal === MODAL_IDS.ABOUT && <AboutModal isOpen={true} onClose={closeModal} />}
                {activeModal === MODAL_IDS.PRIVACY && <PrivacyModal isOpen={true} onClose={closeModal} />}
                {activeModal === MODAL_IDS.TERMS && <TermsModal isOpen={true} onClose={closeModal} />}
                {activeModal === MODAL_IDS.CONTACT && <ContactModal isOpen={true} onClose={closeModal} />}
                {activeModal === MODAL_IDS.SETTINGS && <SettingsModal isOpen={true} onClose={closeModal} />}
            </Suspense>

            <div className="container mx-auto px-4 py-8 max-w-6xl relative z-10">
                {children}
                <Footer />
            </div>

            {/* Floating Settings Button */}
            <button
                onClick={() => openModal(MODAL_IDS.SETTINGS)}
                className="settings-fab fixed bottom-24 left-6 z-40 h-16 w-16 rounded-full hidden md:flex items-center justify-center shadow-lg transition-all duration-300 hover:scale-105 hover:-translate-y-1 focus:outline-none"
                aria-label="Open Settings"
            >
                <CogIcon className="text-2xl settings-icon-spin" />
            </button>

            {/* Floating Guide Button */}
            <button
                onClick={() => openModal(MODAL_IDS.TIPS)}
                className="fixed bottom-6 left-6 z-40 h-16 w-16 bg-gradient-to-br from-neutral-800 to-neutral-950 rounded-full text-white hidden md:flex items-center justify-center shadow-lg transition-all duration-300 hover:scale-105 hover:-translate-y-1 focus:outline-none focus:ring-4 focus:ring-neutral-500/50"
                aria-label="Open Access & Troubleshooting Guide"
            >
                <LightbulbIcon className="text-2xl tips-icon-pulse" />
            </button>

            {/* Scroll to top button */}
            <button
                onClick={scrollToTop}
                className={`fixed bottom-6 right-6 z-40 h-16 w-16 bg-gradient-to-br from-orange-500 to-red-600 rounded-full text-white hidden md:flex items-center justify-center shadow-lg shadow-orange-500/40 transition-all duration-300 ease-in-out hover:scale-105 hover:-translate-y-1 focus:outline-none focus:ring-4 focus:ring-orange-500/50 ${showScrollTop ? 'opacity-100 translate-y-0' : 'opacity-0 translate-y-4 pointer-events-none'}`}
                aria-label="Scroll to top"
            >
                <ChevronUpIcon className="text-2xl" />
            </button>

            {/* Mobile Taskbar */}
            <MobileTaskbar
                scrollToTop={scrollToTop}
                visibleSection={visibleSection}
            />

            {/* PWA Install Prompt */}
            <Suspense fallback={null}>
                <InstallPrompt
                    isOpen={showInstallPrompt}
                    onInstall={handleInstallClick}
                    onClose={() => setShowInstallPrompt(false)}
                />
            </Suspense>

            {/* PWA Update Notification */}
            <Suspense fallback={null}>
                <UpdateNotification
                    isOpen={isUpdateAvailable}
                    onUpdate={handleUpdateClick}
                />
            </Suspense>

            {/* PWA Install Notification Toast (Installing vs Installed States) */}
            <div 
                className="fixed top-6 left-0 right-0 z-[9999] flex justify-center px-4 pointer-events-none transition-all"
                style={{
                    transform: showInstallSuccess ? 'translateY(0) scale(1)' : 'translateY(-100px) scale(0.9)',
                    opacity: showInstallSuccess ? 1 : 0,
                    pointerEvents: showInstallSuccess ? 'auto' : 'none',
                    transition: 'transform 0.5s cubic-bezier(0.175, 0.885, 0.32, 1.275), opacity 0.4s ease'
                }}
            >
                {installStatus === 'installing' ? (
                    <div className="w-full max-w-sm pointer-events-auto flex items-center gap-4 p-4 rounded-2xl bg-[var(--card-bg)] backdrop-blur-xl border border-blue-500/30 shadow-lg shadow-blue-500/15 dark:shadow-blue-500/10">
                        <div className="flex items-center justify-center h-10 w-10 rounded-xl bg-blue-500/10 text-blue-500 border border-blue-500/20 shrink-0">
                            <svg className="animate-spin h-5 w-5 text-blue-500" xmlns="http://www.w3.org/2000/svg" fill="none" viewBox="0 0 24 24">
                                <circle className="opacity-25" cx="12" cy="12" r="10" stroke="currentColor" strokeWidth="4"></circle>
                                <path className="opacity-75" fill="currentColor" d="M4 12a8 8 0 018-8V0C5.373 0 0 5.373 0 12h4zm2 5.291A7.962 7.962 0 014 12H0c0 3.042 1.135 5.824 3 7.938l3-2.647z"></path>
                            </svg>
                        </div>
                        <div className="flex flex-col text-left flex-1 min-w-0">
                            <span className="text-sm font-bold text-[var(--main-text)]">Installing {SITE_NAME}...</span>
                            <span className="text-xs text-[var(--secondary-text)] truncate">Adding application to your device.</span>
                        </div>
                    </div>
                ) : (
                    <div className="w-full max-w-sm pointer-events-auto flex items-center gap-4 p-4 rounded-2xl bg-[var(--card-bg)] backdrop-blur-xl border border-emerald-500/30 shadow-lg shadow-emerald-500/15 dark:shadow-emerald-500/10">
                        <div className="flex items-center justify-center h-10 w-10 rounded-xl bg-emerald-500/10 text-emerald-500 border border-emerald-500/20 shrink-0">
                            <CheckIcon className="h-5 w-5 text-emerald-500" />
                        </div>
                        <div className="flex flex-col text-left flex-1 min-w-0">
                            <span className="text-sm font-bold text-[var(--main-text)]">App Installed</span>
                            <span className="text-xs text-[var(--secondary-text)] truncate">{SITE_NAME} is ready on your Home Screen.</span>
                        </div>
                    </div>
                )}
            </div>
        </>
    );
}
